import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injector } from '@angular/core';
import { LegalEntitiesApi, LegalEntitiesApiClient } from '@sg/api/legal-entities-api';

import { validateArrayType } from './utils/validate-array';
import { SgEntityFetchAdapter } from '../components/entity-select-auto-complete';

const PICKER_PAGE_SIZE = 10;

type LegalEntitiesTypes =
  | LegalEntitiesApi.BorrowerDto
  | LegalEntitiesApi.StandardLenderDto
  | LegalEntitiesApi.SubLenderDto
  | LegalEntitiesApi.SuperLenderDto;

declare module '../components/entity-select-auto-complete/entity-select-fetch-store.service' {
  export interface SgEntitySelectEntityFetchMap {
    LegalEntity: {
      entity: LegalEntitiesTypes;
      query: LegalEntitiesApi.SearchLegalEntitiesRequest;
    };
    LegalEntityUIAdapter: {
      entity: LegalEntitiesTypes;
      query: LegalEntitiesApi.SearchLegalEntitiesRequest;
    };
  }
}

abstract class BaseLegalEntityApiToEntityFetcherLink<T extends 'LegalEntity' | 'LegalEntityUIAdapter'>
  implements SgEntityFetchAdapter<T>
{
  abstract readonly key: T;
  abstract readonly metadata: {
    idField: keyof LegalEntitiesTypes;
    labelField: keyof LegalEntitiesTypes;
    queryField?: { field: keyof LegalEntitiesApi.SearchLegalEntitiesRequest; isArray?: boolean };
  };

  protected readonly legalEntityApi: LegalEntitiesApiClient;

  constructor(injector: Injector) {
    this.legalEntityApi = injector.get(LegalEntitiesApiClient);
  }

  getEntity(ids: string[]) {
    const body: LegalEntitiesApi.SearchLegalEntitiesRequest = {
      ids: validateArrayType(ids),
      skipApprovedEntities: true,
      skipSGFeeSplits: true,
      allowPartialCodeSearchForSingleCode: true,
    };
    return this.legalEntityApi
      .legalEntitiesControllerGetLegalEntitiesBySearchParams(
        { body, pageNumber: 1, orderBy: 'name', sortDirection: 'asc' },
        { observe: 'body' }
      )
      .pipe(map((result) => (result.rows?.length ? result.rows[0] : [])));
  }

  findEntities(query?: LegalEntitiesApi.SearchLegalEntitiesRequest, pageNumber?: number) {
    const partialCode = singlePartialCode(query);
    const search$ = partialCode
      ? this.legalEntityApi
          .legalEntitiesControllerGetLegalEntitiesByPartialCode(
            {
              body: { code: partialCode, companies: query?.companies },
              pageNumber: pageNumber ?? 1,
              pageSize: PICKER_PAGE_SIZE,
            },
            { observe: 'body' }
          )
          .pipe(
            catchError((error) =>
              error instanceof HttpErrorResponse && error.status === HttpStatusCode.NotFound
                ? this.searchBySearchParams(query, pageNumber)
                : throwError(() => error)
            )
          )
      : this.searchBySearchParams(query, pageNumber);

    return search$.pipe(
      map((result) => {
        return {
          data: result.rows ? result.rows : [],
          infiniteScrollInfo: { currentPage: result.paging.pageNumber, totalPages: result.paging.totalPages },
        };
      })
    );
  }

  private searchBySearchParams(query: LegalEntitiesApi.SearchLegalEntitiesRequest | undefined, pageNumber?: number) {
    const body: LegalEntitiesApi.SearchLegalEntitiesRequest = {
      skipApprovedEntities: true,
      skipSGFeeSplits: true,
      allowPartialCodeSearchForSingleCode: true,
    };

    if (query) {
      Object.assign(body, query);
    }

    return this.legalEntityApi.legalEntitiesControllerGetLegalEntitiesBySearchParams(
      { body, pageSize: PICKER_PAGE_SIZE, pageNumber: pageNumber ?? 1, orderBy: 'name', sortDirection: 'asc' },
      { observe: 'body' }
    );
  }
}

const PARTIAL_CODE_QUERY_KEYS: Array<keyof LegalEntitiesApi.SearchLegalEntitiesRequest> = ['codes', 'companies'];

function singlePartialCode(query: LegalEntitiesApi.SearchLegalEntitiesRequest | undefined): string | undefined {
  const code = query?.codes?.length === 1 ? query.codes[0]?.trim() : undefined;
  const onlyCodeAndCompanies = Object.keys(query ?? {}).every((key) =>
    PARTIAL_CODE_QUERY_KEYS.includes(key as keyof LegalEntitiesApi.SearchLegalEntitiesRequest)
  );
  return code && onlyCodeAndCompanies ? code : undefined;
}

export class LegalEntityApiToEntityFetcherLink extends BaseLegalEntityApiToEntityFetcherLink<'LegalEntityUIAdapter'> {
  readonly key: 'LegalEntityUIAdapter' = 'LegalEntityUIAdapter';
  readonly metadata: {
    idField: keyof LegalEntitiesTypes;
    labelField: keyof LegalEntitiesTypes;
    queryField?: { field: keyof LegalEntitiesApi.SearchLegalEntitiesRequest; isArray?: boolean };
  } = {
    idField: 'legalEntityId',
    labelField: 'code',
    queryField: { field: 'codes', isArray: true },
  };

  constructor(injector: Injector) {
    super(injector);
  }
}

export class LegalEntityByNameToEntityFetcherLink extends BaseLegalEntityApiToEntityFetcherLink<'LegalEntity'> {
  readonly key: 'LegalEntity' = 'LegalEntity';
  readonly metadata: {
    idField: keyof LegalEntitiesTypes;
    labelField: keyof LegalEntitiesTypes;
    queryField?: { field: keyof LegalEntitiesApi.SearchLegalEntitiesRequest; isArray?: boolean };
  } = {
    idField: 'legalEntityId',
    labelField: 'name',
    queryField: { field: 'name' },
  };

  constructor(injector: Injector) {
    super(injector);
  }
}
